import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ComponentProps,
  type SyntheticEvent,
} from 'react'
import { Box, Button, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import JoditEditor from 'jodit-react'
import { toast } from 'react-toastify'
import cn from 'classnames'
import { EditorWrapper } from 'Components/UI/TextEditor/style'
import { TextEditorconfig } from 'Components/UI/TextEditor/_config'
import { registerJoditTableResize, type JoditInstanceForTableResize } from 'Components/UI/TextEditor/joditTableResize'
import {
  registerJoditTableMultiCellStyleSync,
  type JoditInstanceForTableMultiCellStyleSync,
} from 'Components/UI/TextEditor/joditTableMultiCellStyleSync'
import { InputHelperText } from 'Components/UI/FormElemnts/style'
import { TextEditorImageUploadDialog, TEXT_EDITOR_CLOUD_IMAGE_ICON } from 'Components/UI/TextEditor/TextEditorImageUploadDialog'


type Props = {
  value: string
  onChange: (value: string) => void,
  onBlur?: () => void
  errorText?: string | boolean
  className?: string
  label: string
}

type JoditEditorRefArg = Parameters<NonNullable<ComponentProps<typeof JoditEditor>['editorRef']>>[0]

type JoditWithDom = JoditEditorRefArg & { container?: HTMLElement }

type JoditLike = { value?: string }

type EditorDisplayMode = 'visual' | 'html'

function getHtmlFromJoditOrProp(
  instance: JoditLike | null,
  propValue: string,
): string {
  const fromInstance = instance?.value
  if (typeof fromInstance === 'string') {
    return fromInstance
  }
  return propValue ?? ''
}

export const TextEditorUI = ({ value, onChange, errorText, className, label, onBlur }: Props) => {
  const editor = useRef(null)
  const joditInstanceRef = useRef<JoditLike | null>(null)
  const cleanupTableResizeRef = useRef<(() => void) | null>(null)
  const cleanupTableMultiCellStyleSyncRef = useRef<(() => void) | null>(null)
  const [isJoditFocused, setIsJoditFocused] = useState(false)
  const [displayMode, setDisplayMode] = useState<EditorDisplayMode>('visual')
  const [serverImageDialogOpen, setServerImageDialogOpen] = useState(false)
  const setJoditFocusedRef = useRef(setIsJoditFocused)
  setJoditFocusedRef.current = setIsJoditFocused

  /**
   * jodit-react destroys and recreates the editor whenever `config` or `editorRef`
   * identity changes (see its useEffect deps). Inline arrows here caused focus loss
   * after each parent re-render (e.g. react-hook-form setValue).
   */
  const config = useMemo(() => {
    const buttons = [...TextEditorconfig.buttons]
    const imageIdx = buttons.indexOf('image')
    if (imageIdx >= 0) {
      buttons.splice(imageIdx + 1, 0, 'tashkecServerImage')
    } else {
      buttons.push('tashkecServerImage')
    }
    return {
      ...TextEditorconfig,
      buttons,
      controls: {
        ...TextEditorconfig.controls,
        tashkecServerImage: {
          iconURL: TEXT_EDITOR_CLOUD_IMAGE_ICON,
          tooltip: 'Загрузить изображение на сервер',
          exec: () => {
            setServerImageDialogOpen(true)
          },
        },
      },
    }
  }, [])

  const onChangeRef = useRef(onChange)
  const onBlurRef = useRef(onBlur)
  onChangeRef.current = onChange
  onBlurRef.current = onBlur

  /**
   * jodit-react syncs `value` → editor in an effect: `if (e.value !== d) e.value = d`.
   * react-hook-form often applies `value` one frame behind fast typing, so `d` can be
   * shorter/stale HTML → caret jumps to start and characters reorder. While focus is
   * inside the editor (or its toolbar), pass `undefined` so that effect skips (`d`
   * is undefined) and the instance keeps the live DOM as source of truth.
   */
  const handleEditorRef = useCallback((instance: JoditEditorRefArg) => {
    joditInstanceRef.current = instance as unknown as JoditLike
    cleanupTableResizeRef.current?.()
    cleanupTableResizeRef.current = registerJoditTableResize(instance as unknown as JoditInstanceForTableResize)
    cleanupTableMultiCellStyleSyncRef.current?.()
    cleanupTableMultiCellStyleSyncRef.current = registerJoditTableMultiCellStyleSync(
      instance as unknown as JoditInstanceForTableMultiCellStyleSync
    )
    const root = (instance as JoditWithDom).container
    if (!root) {
      return
    }
    const setFocused = (v: boolean) => setJoditFocusedRef.current(v)
    const onFocusIn = () => setFocused(true)
    const onFocusOut = () => {
      requestAnimationFrame(() => {
        if (!root.contains(document.activeElement)) {
          setFocused(false)
        }
      })
    }
    root.addEventListener('focusin', onFocusIn)
    root.addEventListener('focusout', onFocusOut)
  }, [])

  const handleChange = useCallback((newContent: string) => {
    onChangeRef.current(newContent)
  }, [])

  const handleBlur = useCallback(() => {
    onBlurRef.current?.()
  }, [])

  const switchToHtmlMode = useCallback(() => {
    const next = getHtmlFromJoditOrProp(joditInstanceRef.current, value ?? '')
    onChangeRef.current(next)
    setDisplayMode('html')
  }, [value])

  const switchToVisualMode = useCallback(() => {
    setDisplayMode('visual')
  }, [])

  const handleHtmlSourceChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    onChangeRef.current(event.target.value)
  }, [])

  const handleCopyHtml = useCallback(async () => {
    const raw =
      displayMode === 'html'
        ? value ?? ''
        : getHtmlFromJoditOrProp(joditInstanceRef.current, value ?? '')
    try {
      await navigator.clipboard.writeText(raw)
      toast.success('HTML copied to clipboard')
    } catch {
      toast.error('Could not copy HTML')
    }
  }, [displayMode, value])

  const handleDisplayModeChange = useCallback(
    (_event: SyntheticEvent, next: EditorDisplayMode | null) => {
      if (next == null) {
        return
      }
      if (next === 'html') {
        switchToHtmlMode()
      } else {
        switchToVisualMode()
      }
    },
    [switchToHtmlMode, switchToVisualMode],
  )

  const handleInsertServerImage = useCallback((imageUrl: string) => {
    const safeAttr = imageUrl.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
    const inst = joditInstanceRef.current as {
      focus?: () => void
      s?: { insertHTML: (html: string) => void }
      value?: string
    } | null
    inst?.focus?.()
    inst?.s?.insertHTML(`<p><img src="${safeAttr}" alt="" style="max-width:100%;height:auto;"/></p>`)
    try {
      const next = inst?.value
      if (typeof next === 'string') {
        onChangeRef.current(next)
      }
    } catch {
      // Jodit version differences
    }
  }, [])

  /**
   * After react-hook-form `reset()` (e.g. edit load), jodit-react can leave the placeholder visible
   * until focus. Sync the instance HTML when not focused so Jodit clears empty state / placeholder.
   */
  useLayoutEffect(() => {
    if (isJoditFocused) {
      return
    }
    const inst = joditInstanceRef.current as { value?: string } | null
    if (!inst) {
      return
    }
    const next = value ?? ''
    try {
      if (inst.value !== next) {
        inst.value = next
      }
    } catch {
      // Jodit API surface differs by version; jodit-react still syncs via `value` prop when this fails.
    }
  }, [value, isJoditFocused])

  useLayoutEffect(() => () => {
    cleanupTableResizeRef.current?.()
    cleanupTableResizeRef.current = null
    cleanupTableMultiCellStyleSyncRef.current?.()
    cleanupTableMultiCellStyleSyncRef.current = null
  }, [])

  return (
    <>
      <Typography variant='subtitle2' marginBottom={1}>{label}</Typography>
      <EditorWrapper className={cn('html-editor-content', className || '', { error: !!errorText })}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1.5,
            mb: 1,
            flexWrap: 'nowrap',
          }}
        >
          <ToggleButtonGroup
            exclusive
            value={displayMode}
            onChange={handleDisplayModeChange}
            aria-label='Editor view'
            size='small'
            sx={{
              flexShrink: 0,
              borderRadius: '10px',
              border: '1px solid',
              borderColor: 'rgba(137, 146, 169, 0.35)',
              backgroundColor: 'rgba(137, 146, 169, 0.06)',
              '& .MuiToggleButtonGroup-grouped': {
                border: 0,
                borderRadius: '8px !important',
                mx: 0.25,
                my: 0.25,
                minWidth: 88,
                height: 32,
                minHeight: 32,
                boxSizing: 'border-box',
                px: 2,
                py: 0,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 13,
                lineHeight: 1.2,
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'primary.contrastText',
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(137, 146, 169, 0.12)',
                },
                '&.Mui-selected:hover': {
                  backgroundColor: 'primary.dark',
                },
              },
            }}
          >
            <ToggleButton value='visual' disableRipple>
              Visual
            </ToggleButton>
            <ToggleButton value='html' disableRipple>
              HTML
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            type='button'
            variant='outlined'
            size='small'
            onClick={handleCopyHtml}
            sx={{
              flexShrink: 0,
              height: 36,
              minHeight: 36,
              px: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 13,
              borderRadius: '10px',
              borderColor: 'rgba(137, 146, 169, 0.35)',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
                backgroundColor: theme => theme.palette.action.hover,
              },
            }}
          >
            Copy HTML
          </Button>
        </Box>
        {/*
          Keep Jodit mounted when switching to HTML mode (hide with CSS). Unmounting destroys the
          instance while custom plugins still run cleanup → Jodit EventEmitter "Need subject" errors.
        */}
        <Box sx={{ display: displayMode === 'visual' ? 'block' : 'none' }}>
          <JoditEditor
            ref={editor}
            value={
              displayMode === 'html' || !isJoditFocused
                ? value
                : undefined
            }
            config={config}
            editorRef={handleEditorRef}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Box>
        <Box sx={{ display: displayMode === 'html' ? 'block' : 'none' }}>
          <TextField
            multiline
            fullWidth
            value={value ?? ''}
            onChange={handleHtmlSourceChange}
            onBlur={handleBlur}
            error={!!errorText}
            placeholder='<!-- Raw HTML -->'
            spellCheck={false}
            InputProps={{
              sx: {
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                fontSize: 13,
                lineHeight: 1.5,
                alignItems: 'flex-start',
                py: 1,
              },
            }}
            sx={{
              '& .MuiInputBase-root': {
                minHeight: 400,
                alignItems: 'stretch',
                borderRadius: '10px',
                border: '1px solid rgba(137, 146, 169, 0.2)',
                backgroundColor: '#fff',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& .MuiInputBase-inputMultiline': {
                py: 1.5,
                px: 2,
              },
            }}
          />
        </Box>
        <InputHelperText className={cn({ show: !!errorText, error: !!errorText })}>{errorText}</InputHelperText>
      </EditorWrapper>
      <TextEditorImageUploadDialog
        open={serverImageDialogOpen}
        onClose={() => setServerImageDialogOpen(false)}
        onInsert={handleInsertServerImage}
      />
    </>
  )
}